import { Component, OnInit } from '@angular/core';
import { Zone } from '../_types/zone';
import { Filter, FilterOperation, HasFilter } from 'src/app/core/jsonapi/filter';
import { ZonesService } from '../_services/zones.service';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { firstValueFrom } from 'rxjs';
import { Country } from '../../common/_types/country';
import { CountryRegion } from '../../common/_types/country-region';
import { CountriesService } from '../../common/_services/countries.service';
import { CountriesRegionsService } from '../../common/_services/countries-regions.service';
import { SessionStorageService } from 'angular-web-storage';
import { FilterFieldConfig } from 'src/app/core/filter-panel/filter-field.model';
import { LoginService } from '../../login/login.service';

@Component({
    selector: 'app-zones-list',
    templateUrl: './zones-list.component.html',
    styleUrls: ['./zones-list.component.scss'],
    host: { class: 'list-page' },
    standalone: false
})
export class ZonesListComponent implements OnInit {
  public zones: Zone[] = [];
  public countries: Country[] = [];
  public countriesRegions: CountryRegion[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  private lastGridEvent: any;
  public filters: any = {
    name: new Filter(FilterOperation.contains, 'name'),
    countryId: new Filter(FilterOperation.equals, 'countryId'),
    countryRegionId: new Filter(FilterOperation.equals, 'countryRegionId'),
    zipCode: new HasFilter('zoneLines', new Filter(FilterOperation.contains, 'value')) // new Filter(FilterOperation.contains, 'zoneLines.value'),
  };

  public countryOptions: { label: string; value: any }[] = [];
  public regionOptions: { label: string; value: any }[] = [];
  private lastCountryId: any = undefined;
  public fieldsConfig: FilterFieldConfig[] = [
    { filter: this.filters.name, type: 'text', label: 'Nombre', base: true },
    { filter: this.filters.zipCode, type: 'text', label: 'Código postal', base: true },
    { filter: this.filters.countryId, type: 'select', label: 'País', options: this.countryOptions, filterable: true, base: true },
    { filter: this.filters.countryRegionId, type: 'select', label: 'Provincia', options: this.regionOptions, filterable: true, base: true }
  ];
  public allowWrite: boolean = false;
  public allowDelete: boolean = false;


  constructor(
    private zonesService: ZonesService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public topbarService: TopbarService,
    private countriesService: CountriesService,
    private countriesRegionsService: CountriesRegionsService,
    private sessionStorage: SessionStorageService,
    private loginService: LoginService
  ) {
  
    this.allowWrite = this.loginService.hasPermission("ZONES.WRITE");
    this.allowDelete = this.loginService.hasPermission("ZONES.DELETE");
  }

  ngOnInit(): void {
    this.topbarService.setTitle("Zonas");
    this.topbarService.addAction("Nueva", "pi pi-plus", false, () => {
      this.edit({});
    });

    //recuperar filtros guardados
    let savedFilters = this.sessionStorage.get("zonesFilters");
    if(savedFilters!=null) FilterBuilder.loadSavedFilters(this.filters, savedFilters);

    this.loadCountries();
    this.refreshRegions();
  } 
  
  async loadCountries() {
    let params: IParamsCollection = {
      page: {
        size: 1000
      },
      sort: [ 'name' ]
    }
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
    this.fillOptions(this.countryOptions, response.data);
  }

  private refreshRegions() {
    if (this.filters.countryId.value === this.lastCountryId) return;
    this.lastCountryId = this.filters.countryId.value;
    this.loadCountryRegions(this.filters.countryId.value);
  }

  private fillOptions(target: { label: string; value: any }[], items: any[]) {
    target.length = 0;
    target.push(...items.map(item => ({ label: item.name, value: item.id })));
  }

  onCountryChange(event: any) {
    let countryId = event.value;
    this.loadCountryRegions(countryId);
  }

  async loadCountryRegions(countryId: string | null = null) {
    let params: IParamsCollection = {
      page: {
        size: 1000
      },
      sort: [ 'name' ]
    }
    if(countryId){
      let filters: any = {
        countryId: new Filter(FilterOperation.equals, "countryId", '', countryId)
      };
      params.filter = FilterBuilder.fromObject(filters).build();
    }
    let response = await firstValueFrom(this.countriesRegionsService.all(params));
    this.countriesRegions = response.data;
    this.fillOptions(this.regionOptions, response.data);
  }

  async loadData($event: any) {
    if(this.filters.zipCode.value!=null && this.filters.zipCode.value!=''){
      this.filters.zipCode.filter.value = this.filters.zipCode.value;
    }else{
      this.filters.zipCode.filter.value = null;
    }
    this.lastGridEvent = $event;
    console.log(this.filters.zipCode.filter.value);

    let params: IParamsCollection = {
      page: { number: $event.first + 1, size: $event.rows },
      include: ['country', 'countryRegion', 'zoneLines'],
    }

    if (typeof $event.sortField != "undefined") params.sort = [($event.sortOrder > 0 ? "-" : "") + $event.sortField];

    //guardamos los filtros en la sesion
    this.sessionStorage.set("zonesFilters", this.filters);

    //creamos filtros
    params.filter = FilterBuilder.fromObject(this.filters).build();

    let response = await firstValueFrom(this.zonesService.all(params));
    this.zones = response.data;
    this.totalRecords = response.meta['total'];
    this.topbarService.setCount(this.totalRecords);
  }

  clearFilters() {
    this.filters = {};
    this.search();
  }

  search() {
    this.refreshRegions();
    this.lastGridEvent.first = 0;
    this.loadData(this.lastGridEvent);
  }

  edit(zone: any) {
    this.router.navigate(['zones/' + (zone && Object.keys(zone).length > 0 ? zone.id : 0)]);
  }

  delete(zone: any) {
    if (this.allowDelete) {
      this.confirmationService.confirm({
        message: "¿Está seguro que desea eliminar la zona?",
        header: "Eliminar zona",
        acceptLabel: "Aceptar",
        rejectLabel: "Cancelar",
        acceptButtonStyleClass: "p-button-danger",
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.zonesService.delete(zone.id).subscribe({
            next: (data: any) => {
              this.messageService.add({ closable: true, severity: 'success', detail: "ZOna eliminada correctamente" });
              this.loadData(this.lastGridEvent);
            },
            error: (error: any) => {
              this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
            }
          });
        },
        reject: (error: any) => {
          this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
        }
      });
    }
  }
}
